/**
 * ゲームフィールの共通部品(docs/15_game_feel.md §2 の A〜D)。
 *
 * まなびで先に作った「順番ポップイン / キーバッジ / 星バースト / キー操作」を
 * **ここ1か所**にまとめて、タイピング・パズルの森・まなびの3モードから使う
 * (同じ演出を3回書かない = 直すときも1か所)。
 *
 * 守っていること:
 *   - 動かすのは **transform / opacity だけ**(Chromebook 30fps 要件。CLAUDE.md ルール6)
 *   - `prefers-reduced-motion: reduce` では演出を止める(CSS 側で丸ごと)
 *   - **演出中でもクリック・キー入力は即受付**。アニメは見た目だけで、
 *     入力の可否を一切ゲートしない(`pointer-events` も触らない)
 *   - 色はフラット単色。グラデーションは使わない(CLAUDE.md ルール7)
 */

import { useEffect, useRef, type CSSProperties, type KeyboardEvent, type RefCallback } from 'react';
import './gameFeel.css';

/** 順番ポップインのずらし幅(ms。docs/15 §2C の 30〜60ms)。 */
export const POP_STEP_MS = 40;

/**
 * 順番ポップインの遅延(docs/15 §2C)。
 *
 * `className="gf-pop-in"` と一緒に使う。要素が多い画面(ステージ選択など)で
 * 最後の1枚がいつまでも出てこないと「遅い」になるので、**遅延には上限**を置く。
 *
 * @param index 並びの中での位置(0 始まり)
 * @param stepMs 1つあたりのずらし幅
 * @param maxMs 遅延の上限(これ以上は待たせない)
 */
export function popInDelay(index: number, stepMs = POP_STEP_MS, maxMs = 320): CSSProperties {
  return { animationDelay: `${Math.min(index * stepMs, maxMs)}ms` };
}

/**
 * キーバッジ(docs/15 §2A の角丸キー様式)。
 *
 * **必ず `aria-hidden`**。ボタンの読み上げ名は文言だけにしておきたい
 * (「もういちど Enter」と読ませない。E2E のボタン名一致も壊れない)。
 *
 * @param corner カードの右上に重ねる(親に `position: relative` が要る)
 */
export function KeyBadge({
  keyLabel,
  corner = false,
  className,
}: {
  readonly keyLabel: string;
  readonly corner?: boolean;
  readonly className?: string;
}) {
  return (
    <span
      className={['gf-key-badge', corner ? 'corner' : '', className ?? '']
        .filter(Boolean)
        .join(' ')}
      aria-hidden="true"
    >
      {keyLabel}
    </span>
  );
}

/**
 * 星バースト(docs/15 §2B のマイクロ報酬)。
 *
 * 小さな星を放射状に弾けさせる。位置は角度から決め打ちなので毎回同じ
 * (乱数を使わない = 見え方が安定する)。動かすのは transform / opacity だけ。
 *
 * @param stars 星の数(0 なら何も出さない)。1つにつき4つ弾ける
 * @param radius 飛ぶ距離(px)
 */
export function StarBurst({
  stars,
  radius = 90,
  className,
}: {
  readonly stars: number;
  readonly radius?: number;
  readonly className?: string;
}) {
  const count = Math.max(0, Math.round(stars)) * 4;
  if (count === 0) return null;
  return (
    <div className={['gf-burst', className ?? ''].filter(Boolean).join(' ')} aria-hidden="true">
      {Array.from({ length: count }, (_, i) => {
        const angle = (i / count) * Math.PI * 2;
        return (
          <span
            key={i}
            className="gf-burst-star"
            style={
              {
                '--burst-x': `${Math.round(Math.cos(angle) * radius)}px`,
                '--burst-y': `${Math.round(Math.sin(angle) * radius)}px`,
                animationDelay: `${i * 30}ms`,
              } as CSSProperties
            }
          >
            ⭐
          </span>
        );
      })}
    </div>
  );
}

/** ステージ・カードを直接えらぶキー(1〜9・0 = 10番目)。 */
export const CHOICE_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'] as const;

/** 文字を打ち込んでいる最中(入力欄・contenteditable)か。 */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

/** 修飾キー・リピート・すでに処理済みのイベントを弾く(共通の門番)。 */
function isPlainKey(event: KeyboardEvent | globalThis.KeyboardEvent): boolean {
  if (event.repeat || event.defaultPrevented) return false;
  if (event.metaKey || event.ctrlKey || event.altKey) return false;
  return !isTypingTarget(event.target);
}

/**
 * 数字キーで選択肢を直接えらぶ(docs/15 §2A)。
 *
 * 画面がマウントされているあいだだけ効く(`window` に張って、外れるときに外す)。
 * `onPick` は**並びの中での位置**(0 始まり)を受け取る。押せない項目の判定は
 * 呼び出し側の責任(ロック中のカードでは何もしないで返す)。
 */
export function useChoiceKeys(
  keys: readonly string[],
  onPick: (index: number) => void,
  enabled = true,
): void {
  const pick = useRef(onPick);
  pick.current = onPick;

  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: globalThis.KeyboardEvent): void => {
      if (!isPlainKey(event)) return;
      const index = keys.indexOf(event.key);
      if (index < 0) return;
      event.preventDefault();
      pick.current(index);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [keys, enabled]);
}

/**
 * Enter / Escape のような1発キー(docs/15 §2A の「リザルトもキーで完結」)。
 *
 * Enter は**ボタンにフォーカスがあるときは何もしない**。`<button>` が自分で
 * click に変えてくれるので、ここでも拾うと2回動いてしまう。
 */
export function useHotkey(key: string, handler: () => void, enabled = true): void {
  const run = useRef(handler);
  run.current = handler;

  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: globalThis.KeyboardEvent): void => {
      if (event.key !== key || !isPlainKey(event)) return;
      if (key === 'Enter') {
        const active = document.activeElement;
        if (active instanceof HTMLButtonElement || active instanceof HTMLAnchorElement) return;
      }
      event.preventDefault();
      run.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [key, enabled]);
}

/**
 * ←→↑↓ でボタンのあいだを移動する(Enter は `<button>` がそのまま受ける)。
 *
 * 使いかた:
 * ```tsx
 * const arrows = useArrowFocus<HTMLButtonElement>();
 * <div onKeyDown={arrows.onKeyDown}>
 *   {items.map((item, i) => <button ref={arrows.register(i)} … />)}
 * </div>
 * ```
 * 押せない(`disabled`)ボタンは飛ばす。端まで来たら止まる(輪にしない —
 * 端で急に反対側へ飛ぶと、どこにいるか分からなくなる)。
 */
export function useArrowFocus<T extends HTMLElement>(): {
  readonly register: (index: number) => RefCallback<T>;
  readonly onKeyDown: (event: KeyboardEvent) => void;
  readonly focusAt: (index: number, options?: FocusOptions) => void;
} {
  const nodes = useRef<(T | null)[]>([]);

  const register =
    (index: number): RefCallback<T> =>
    (node) => {
      nodes.current[index] = node;
    };

  /** 画面を跳ねさせたくないときは `{ preventScroll: true }` を渡す。 */
  const focusAt = (index: number, options?: FocusOptions): void => {
    nodes.current[index]?.focus(options);
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    const step =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? -1
          : 0;
    if (step === 0 || event.metaKey || event.ctrlKey || event.altKey) return;
    const list = nodes.current;
    const current = list.findIndex((node) => node !== null && node === document.activeElement);
    if (current < 0) return;
    for (let next = current + step; next >= 0 && next < list.length; next += step) {
      const node = list[next];
      if (!node || (node as HTMLElement & { disabled?: boolean }).disabled) continue;
      event.preventDefault();
      node.focus();
      return;
    }
  };

  return { register, onKeyDown, focusAt };
}
